import { logError } from '../log';

import { getAttribution } from './attribution';
import hasEffectiveConsent from './effectiveConsent';
import { collectTrackingIds } from './trackingIds';

/**
 * Collects the consent + attribution context the server persists next to a
 * purchase (see server/api-util/transactionTracking.js). Used by every flow
 * that hands a conversion off to the backend — the booking checkout as well as
 * the hosted Stripe pass checkouts, where no thank-you page exists to fire a
 * client-side purchase on.
 *
 * Degrades to `undefined` on any failure: tracking must never break a purchase
 * flow, and the server treats a missing context as "no consent" (fail-closed).
 *
 * @param {{ listingTitle?: string }} [options]
 * @returns {Promise<{
 *   consent: { marketing: boolean, statistics: boolean },
 *   ids: ReturnType<typeof collectTrackingIds>,
 *   attribution: ReturnType<typeof getAttribution>,
 *   eventSourceUrl: string|undefined,
 *   listingTitle?: string,
 * }|undefined>}
 */
export const collectTrackingContext = async ({ listingTitle } = {}) => {
  try {
    const [marketing, statistics] = await Promise.all([
      hasEffectiveConsent('marketing'),
      hasEffectiveConsent('statistics'),
    ]);
    return {
      consent: { marketing, statistics },
      ids: collectTrackingIds(),
      attribution: getAttribution(),
      eventSourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      ...(listingTitle ? { listingTitle } : {}),
    };
  } catch (e) {
    logError(e, 'tracking-context-failed');
    return undefined;
  }
};
